/* eslint-disable jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events, react/prop-types */
import clsx from 'clsx'
import { get, isNil } from 'lodash'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import PerfectScrollbar from 'react-perfect-scrollbar'
import Select, { components, createFilter } from 'react-select'
import CreatableSelect from 'react-select/creatable'
import { Checkbox } from '../Checkbox'
import Menu from './Menu/Menu'
import './MultiSelectSearch.module.scss'
import multiSelectSearchStyles from './MultiSelectSearchStyles'

const PERFECT_SCROLLBAR_OPTIONS = {
  wheelSpeed: 1,
  swipeEasing: true,
  wheelPropagation: false,
  minScrollbarLength: 60,
  scrollingThreshold: 1000,
  // Options wrap, so the list only ever scrolls vertically.
  suppressScrollX: true,
}

function MenuList(props) {
  const { children, selectProps, ...rest } = props

  return (
    <components.MenuList {...rest} selectProps={selectProps}>
      <PerfectScrollbar
        className="scroll"
        data-height="240"
        data-mobile-height="240"
        data-scroll="true"
        options={PERFECT_SCROLLBAR_OPTIONS}
        // PerfectScrollbar owns the actual scroll container, so pass its end event back to react-select.
        onYReachEnd={() => selectProps.onMenuScrollToBottom?.()}
        style={{
          maxHeight: 235,
          padding: '5px 0',
          backgroundColor: 'var(--aug-surface-raised, var(--aug-ui-bg, #ffffff))',
        }}
      >
        {children}
      </PerfectScrollbar>
    </components.MenuList>
  )
}

function Option(props) {
  const { isSelected, children, data, selectProps } = props

  // In creatable mode, options are only ever the values already attached — there's nothing else
  // to browse and toggle, so a checkbox here would always read as checked and adds no information
  // (worse, on the "Create <value>" entry it implies a toggle for something that doesn't exist yet).
  return (
    <components.Option {...props}>
      <div className="Uic-MultiSelectSearch-option">
        {selectProps.creatable ? (
          children
        ) : (
          <Checkbox className="Uic-MultiSelectSearch-centerCB" readOnly checked={isSelected} label={children} />
        )}
        {!isNil(data.count) ? <div className="Uic-MultiSelectSearch-count">{data.count}</div> : null}
      </div>
    </components.Option>
  )
}

const CLEAR_KEYS = ['Enter', ' ']
const CLEAR_MESSAGE = { id: 'UIC.MULTI_SELECT_SEARCH.CLEAR', defaultMessage: 'Clear selection' }
// Same as react-select's: a touch that moves further than this is a drag, not a tap.
const TOUCH_MOVE_THRESHOLD = 5

const isPastTouchThreshold = (start, touch) =>
  Math.abs(touch.clientX - start.clientX) > TOUCH_MOVE_THRESHOLD ||
  Math.abs(touch.clientY - start.clientY) > TOUCH_MOVE_THRESHOLD

// The chip's ✕. It acts on press, like react-select's own clear indicator: the control opens the
// menu on mousedown, so a click handler would run too late — the menu has already opened, and when
// that re-renders the table header the click can be lost and the filter stays applied. A touch
// that drags off the ✕ is ignored, since its touchend still lands here.
function ClearButton({ onClear }) {
  const intl = useIntl()
  const touchStartRef = useRef(null)
  const isDraggingRef = useRef(false)

  const clear = (event) => {
    event.preventDefault()
    event.stopPropagation()
    onClear()
  }

  return (
    <button
      type="button"
      className="fas fa-xmark p-2 Uic-MultiSelectSearch-clear"
      aria-label={intl.formatMessage(CLEAR_MESSAGE)}
      onMouseDown={(event) => event.button === 0 && clear(event)}
      onTouchStart={(event) => {
        const touch = event.touches.item(0)
        touchStartRef.current = touch && { clientX: touch.clientX, clientY: touch.clientY }
        isDraggingRef.current = false
      }}
      onTouchMove={(event) => {
        const touch = event.touches.item(0)
        if (touchStartRef.current && touch && isPastTouchThreshold(touchStartRef.current, touch)) {
          isDraggingRef.current = true
        }
      }}
      onTouchEnd={(event) => !isDraggingRef.current && clear(event)}
      onKeyDown={(event) => CLEAR_KEYS.includes(event.key) && clear(event)}
    />
  )
}

ClearButton.propTypes = {
  onClear: PropTypes.func.isRequired,
}

function ValueContainer(props) {
  const { children, clearValue, options, selectProps, setValue } = props

  const length = get(selectProps, 'value.length')
  const placeholder = get(selectProps, 'placeholder')
  const firstLabel = get(selectProps, 'value[0].label')
  const inputValue = get(selectProps, 'inputValue')
  const clearValueRestoresAll = get(selectProps, 'clearValueRestoresAll')

  const [, input] = children

  const onClear = useCallback(
    () => (clearValueRestoresAll ? setValue(options) : clearValue()),
    [clearValueRestoresAll, clearValue, options, setValue],
  )

  let value = null
  if (length === 1) {
    value = firstLabel
  } else if (length > 1) {
    value = length
  }

  return (
    <div className="Uic-MultiSelectSearch-valueContainer">
      {value && (
        <div className={clsx('Uic-MultiSelectSearch-pill', inputValue && 'd-none')}>
          {/* A long label is cut off with an ellipsis; the title shows it in full on hover. */}
          <span
            className="Uic-MultiSelectSearch-value"
            title={length === 1 && typeof firstLabel === 'string' ? firstLabel : undefined}
          >
            {value}
          </span>
          <ClearButton onClear={onClear} />
        </div>
      )}
      <span className={clsx('Uic-MultiSelectSearch-label', inputValue && 'd-none')}>{placeholder}</span>
      {input}
    </div>
  )
}

const filterByLabel = createFilter({
  matchFrom: 'any',
  stringify: (option) => `${option.label}`,
})

// A creatable select's synthetic "create <value>" option must always survive filtering,
// regardless of what its label says (see GroupFieldRequirementControl's static create label) —
// otherwise react-select's own label-substring filterOption throws it away the moment the label
// stops containing the typed text.
// eslint-disable-next-line no-underscore-dangle -- react-select's own convention for its synthetic create-option
const filterOption = (option, rawInput) => option.data.__isNew__ || filterByLabel(option, rawInput)

// Caller overrides are layered on the shared styles. An override receives the shared result, not
// react-select's defaults, so it can change one key (for example the menu width) without dropping
// the rest.
const mergeSelectStyles = (baseStyles, overrides) => {
  if (!overrides) {
    return baseStyles
  }
  const merged = { ...baseStyles }
  Object.keys(overrides).forEach((key) => {
    const base = baseStyles[key]
    const override = overrides[key]
    merged[key] = (provided, state) => {
      const baseResult = typeof base === 'function' ? base(provided, state) : { ...provided, ...base }
      const overrideResult = typeof override === 'function' ? override(baseResult, state) : override
      return { ...baseResult, ...overrideResult }
    }
  })
  return merged
}

const getSelectedOptions = (options, value) => {
  let allOptions = options

  // is grouped
  if (options?.[0]?.options) {
    allOptions = options.reduce((acc, item) => acc.concat(item.options), [])
  }

  return allOptions?.filter((option) => value.includes(option.value)) ?? []
}

function MultiSelectSearch(props) {
  const {
    id,
    name,
    placeholder,
    options,
    remoteValue,
    onChange,
    getFilter = () => {},
    clearValueRestoresAll = false,
    creatable = false,
    styles: styleOverrides,
    ...rest
  } = props
  const [value, setValue] = useState([])
  const SelectComponent = creatable ? CreatableSelect : Select

  useEffect(() => {
    if (remoteValue !== undefined) {
      setValue(remoteValue ?? [])
    }
  }, [remoteValue])

  const internalOnChange = (val) => {
    setValue(val)
    onChange(val)
  }

  useEffect(() => {
    if (getFilter) {
      getFilter(internalOnChange)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const selectStyles = useMemo(() => mergeSelectStyles(multiSelectSearchStyles, styleOverrides), [styleOverrides])

  return (
    <div onClick={(event) => event.stopPropagation()} className="Uic-MultiSelectSearch-wrapper">
      <SelectComponent
        id={id}
        name={name}
        placeholder={placeholder}
        options={options}
        isMulti
        autoBlur
        isSearchable
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        isClearable={false}
        value={getSelectedOptions(options, value)}
        onChange={(selectedOptions) => {
          const newValue = selectedOptions?.map((option) => option.value) ?? []
          internalOnChange(newValue)
        }}
        styles={selectStyles}
        classNamePrefix="Uic-MultiSelectSearch"
        creatable={creatable}
        components={{ Menu, MenuList, Option, ValueContainer }}
        filterOption={filterOption}
        clearValueRestoresAll={clearValueRestoresAll}
        {...rest}
      />
    </div>
  )
}

export default MultiSelectSearch
